import { useEffect, useRef, useState } from 'react';
import {
  focusManagement,
  keyboardNavigation,
  motionPreferences,
  announceToScreenReader,
} from "../utils/accessibility";

/**
 * Custom hook for managing accessibility features
 */
export const useAccessibility = () => {
  const [reducedMotion, setReducedMotion] = useState(
    motionPreferences.prefersReducedMotion()
  );
  const [highContrast, setHighContrast] = useState(false); // Simplified for now

  useEffect(() => {
    // Listen for changes in motion preferences
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const contrastQuery = window.matchMedia("(prefers-contrast: high)");

    const handleMotionChange = (e) => setReducedMotion(e.matches);
    const handleContrastChange = (e) => setHighContrast(e.matches);

    motionQuery.addEventListener("change", handleMotionChange);
    contrastQuery.addEventListener("change", handleContrastChange);

    return () => {
      motionQuery.removeEventListener("change", handleMotionChange);
      contrastQuery.removeEventListener("change", handleContrastChange);
    };
  }, []);

  return {
    reducedMotion,
    highContrast,
    announceToScreenReader,
  };
};

/**
 * Hook for managing modal accessibility
 */
export const useModalAccessibility = (isOpen, onClose) => {
  const modalRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Trap focus within modal
      cleanupRef.current = focusManagement.trapFocus(modalRef.current);

      // Handle escape key
      const handleEscape = (e) => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);

      // Prevent body scroll
      document.body.style.overflow = "hidden";

      // Announce modal opening to screen readers
      announceToScreenReader("Modal opened", "assertive");

      return () => {
        // Clean up event listeners
        if (cleanupRef.current) {
          cleanupRef.current();
        }
        document.removeEventListener("keydown", handleEscape);

        // Restore body scroll
        document.body.style.overflow = "auto";

        // Announce modal closing
        announceToScreenReader("Modal closed", "assertive");
      };
    }
  }, [isOpen, onClose]);

  return modalRef;
};

/**
 * Hook for managing keyboard navigation in lists/grids
 */
export const useKeyboardNavigation = (items, onSelect) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  useEffect(() => {
    if (items.length === 0) return;

    const handleKeyDown = (e) => {
      const newIndex = keyboardNavigation.handleArrowKeys(
        e,
        items,
        focusedIndex,
        "horizontal"
      );

      if (newIndex !== focusedIndex) {
        setFocusedIndex(newIndex);
      }

      if (e.key === "Enter" && focusedIndex >= 0) {
        e.preventDefault();
        onSelect(items[focusedIndex]);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [items, focusedIndex, onSelect]);

  const setItemRef = (index) => (ref) => {
    itemRefs.current[index] = ref;
  };

  return {
    focusedIndex,
    setFocusedIndex,
    setItemRef,
  };
};

/**
 * Hook for managing search accessibility
 */
export const useSearchAccessibility = (searchResults, searchTerm) => {
    const [searchAnnounced, setSearchAnnounced] = useState(false);

    useEffect(() => {
        if (searchTerm && !searchAnnounced) {
            const timer = setTimeout(() => {
                const resultCount = searchResults.length;
                const message = resultCount === 0
                    ? 'No results found'
                    : `${resultCount} result${resultCount === 1 ? '' : 's'} found`;

                announceToScreenReader(message, 'polite');
                setSearchAnnounced(true);
            }, 500); // Debounce announcements

            return () => clearTimeout(timer);
        } else if (!searchTerm) {
            setSearchAnnounced(false);
        }
    }, [searchResults.length, searchTerm, searchAnnounced]);

    return {
        announceSearchResults: (count) => {
            const message = count === 0
                ? 'No results found'
                : `${count} result${count === 1 ? '' : 's'} found`;
            announceToScreenReader(message, 'polite');
        }
    };
};